import { Column } from "primereact/column";
import { DataTableSelectEvent } from "primereact/datatable";
import { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";
import {
  attemptShowErrorsInSnackbar,
  useSnackbarActionContext,
} from "../../../context/SnackbarContext";
import { Table, PageHeader } from "@velocity-ehs/component-library";
import {
  getPagination,
  QuerySort,
  SortDirection,
} from "../../supportFunctions/graphql/Queries";
import { ssoIncomingListQuery } from "../../supportFunctions/graphql/SSOIncomingQueries";
import { useTranslation } from "react-i18next";
import UsersService from "@velocity-ehs/user-utils-module";
import AuthService from "./services/AuthService";

type MetadatasProps = {
  editPermission: Array<string>;
};

const Metadatas = ({ editPermission }: MetadatasProps) => {
  const { showSnackbar } = useSnackbarActionContext();

  const history = useHistory();
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);

  const [sso, setSso] = useState([]);

  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 20,
    page: 0,
    sortField: "entityId",
    sortOrder: SortDirection.ASC,
  });

  const onLazyLoad = async () => {
    setIsLoading(true);

    const querySort =
      lazyParams.sortField && lazyParams.sortOrder
        ? {
            sortField: lazyParams.sortField,
            sortDirection: lazyParams.sortOrder,
          }
        : ({} as QuerySort);

    const paginationInput = getPagination(
      lazyParams.page + 1,
      lazyParams.rows,
      querySort
    );

    const filters = {
      or: {
        customerId: [UsersService.customer.id],
      },
    };

    try {
      const response = await AuthService.graphql().post(
        "",
        ssoIncomingListQuery(filters, paginationInput)
      );

      const data = response.data.data.SSOIncomings;

      setTotalRecords(data.pageInfo.totalElements);
      setSso(data.items);
    } catch (error) {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    } finally {
      setIsLoading(false);
    }
  };

  const openAddSSO = (): void => history.push("/singleSignOn/add");

  const onChange = (event: any) => {
    setLazyParams(event);
  };

  const onRowSelect = (event: DataTableSelectEvent): void =>
    history.push(`/singleSignOn/${event.data.id}/edit`);

  const handleDownloadMetadata = () => {
    window.open(`${process.env.REACT_APP_AUTH_API_URL}saml/metadata`);
  };

  useEffect(() => {
    onLazyLoad();
    // eslint-disable-next-line
  }, [lazyParams]);

  return (
    <>
      <PageHeader
        title={t("universal_Single_Sign_On")}
        navigationTree={[
          { text: `Accelerate ${t("universal_Home")}`, href: "/" },
          { text: t("universal_Single_Sign_On"), href: "" },
        ]}
        primaryButton={{
          label: t("universal_Add"),
          id: "addsinglesignon",
          onClick: openAddSSO,
        }}
        secondaryButton={{
          label: t("single_sign_on_download"),
          id: "downloadSingleSignOn",
          onClick: handleDownloadMetadata,
        }}
      />
      <div className="card__fullContent">
        <Table
          data={sso}
          params={lazyParams}
          totalRecords={totalRecords}
          selectionMode="single"
          onRowSelect={onRowSelect}
          onPage={onChange}
          onSort={onChange}
          onFilter={onChange}
          isLoading={isLoading}
          showFilters={false}
        >
          <Column
            field="entityId"
            header={t("application_Config_Issuer")}
            sortable
          />
          <Column
            field="ssoURL"
            header={t("application_Config_Details")}
            body={(props) => <Link to={props.ssoURL}>Test Configuration</Link>}
            sortable
          />
          <Column
            field="customer.name"
            header={t("universal_Customer")}
            sortable
          />
        </Table>
      </div>
    </>
  );
};

export default Metadatas;
